import React, { useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { loadUser } from '../actions/registerUser';
import { RESERVED_USERNAMES } from '../utils/reservedUsernames';
import './UsernameChangeControl.css';

const errorMessage = (err, fallback) => {
  const data = err.response && err.response.data;
  if (data && typeof data.msg === 'string') return data.msg;
  return fallback;
};

// A deliberate, standalone action -- never a side effect of saving the
// Name field anymore (see route/api/users.js's own comment on
// POST /update_user for why that coupling was removed). Deliberately
// self-contained, same reasoning as EmailChangeControl.js: never writes
// into EditProfile's own `User` local state, so a change here can never
// ride along with (or be overwritten by) an unrelated bio/social/
// password submit through the big form's single submit button, and
// vice versa.
const UsernameChangeControl = ({ currentUsername, loadUser }) => {
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [draft, setDraft] = useState(currentUsername || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = () => {
    setDraft(currentUsername || '');
    setError('');
    setMode('edit');
  };

  const cancel = () => {
    setMode('view');
    setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    // This form is nested inside EditProfile.js's big Settings-tab
    // <form> (see UsernameChangeControl's own header comment on why
    // that's deliberate). preventDefault() alone only stops the native
    // navigate-on-submit -- the submit event still bubbles past it to
    // that outer form's onSubmit, which would fire a second, stray
    // POST /update_user carrying the whole stale profile (including
    // socialEntries with explicit null labels, which that route
    // rejects). stopPropagation() is what actually stops it there.
    e.stopPropagation();
    setError('');

    // Immediate feedback only -- same normalization /update_username
    // itself applies (whitespace-stripped, lowercased). The server is
    // the real check (also catches an admin colliding a reserved word
    // onto this account); this just saves a round trip for the common
    // case.
    const normalized = draft.replace(/\s+/g, '').toLowerCase();
    if (RESERVED_USERNAMES.includes(normalized)) {
      setError("This username isn't available.");
      return;
    }

    setSaving(true);
    try {
      await axios.post('/api/users/update_username', { userName: draft });
      // Refreshes redux's user.userName, which also flows into
      // EditProfile's own local `User` state on its next render (see its
      // useEffect keyed off the user prop) -- so the "Back" link and
      // anything else reading the username pick up the new value without
      // this component reaching into that state itself.
      loadUser();
      setMode('view');
      toast.success('Username updated.');
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (mode === 'view') {
    return (
      <div className="form-con">
        <span>Username</span>
        <div className="username-change__current">
          <input
            type="text"
            value={currentUsername || ''}
            placeholder="Not set"
            disabled
          />
          <button type="button" className="username-change__link" onClick={startEdit}>
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-con">
      <span>Username</span>
      <form onSubmit={save}>
        {error && <p className="username-change__error">{error}</p>}
        <input
          type="text"
          className="username-change__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
        <div className="username-change__actions">
          <button type="submit" className="username-change__submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="username-change__cancel"
            onClick={cancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default connect(null, { loadUser })(UsernameChangeControl);
